import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, X, Search, MoreVertical, Edit2, Trash2, Calendar, FileText, Upload, Tv, Grid, List } from 'lucide-react';
import { fetchFamilies } from '../services/familiesApi';
import { fetchAppliances, createAppliance, updateAppliance, deleteAppliance } from '../services/appliancesApi';
import MemberAvatar from './MemberAvatar';
import UserAvatar from './UserAvatar';
import MemberSelector from './MemberSelector';
import Premium from './Premium';
import NavBar from './NavBar';

const Appliances = () => {
    const navigate = useNavigate();
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // grid or list
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [editingAppliance, setEditingAppliance] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);
    
    // Form states
    const [name, setName] = useState('');
    const [brand, setBrand] = useState('');
    const [model, setModel] = useState('');
    const [uniqueId, setUniqueId] = useState('');
    const [applianceType, setApplianceType] = useState('Kitchen');
    const [appliance, setAppliance] = useState('');
    const [purchaseDate, setPurchaseDate] = useState('');
    const [serviceDates, setServiceDates] = useState([{ date: '', recurrence: 'none' }]);
    const [documentOption, setDocumentOption] = useState('none'); // none, upload, select
    const [uploadedDocument, setUploadedDocument] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [sharedWith, setSharedWith] = useState([]);
    const [notes, setNotes] = useState('');
    
    // Appliances data
    const [appliances, setAppliances] = useState([]);
    const [documents, setDocuments] = useState([]); // Would come from Documents page in real app
    
    const fileInputRef = useRef(null);

    const applianceTypes = [
        'Kitchen',
        'Living Room',
        'Bedroom',
        'Bathroom',
        'Portico',
        'Garage',
        'Office',
        'Garden',
        'Other'
    ];

    const applianceCategories = {
        'Kitchen': ['Refrigerator', 'Microwave', 'Oven', 'Dishwasher', 'Coffee Maker', 'Toaster', 'Blender', 'Other'],
        'Living Room': ['TV', 'Air Conditioner', 'Sound System', 'Gaming Console', 'Fan', 'Heater', 'Other'],
        'Bedroom': ['TV', 'Air Conditioner', 'Fan', 'Heater', 'Alarm Clock', 'Other'],
        'Bathroom': ['Water Heater', 'Exhaust Fan', 'Hair Dryer', 'Washing Machine', 'Other'],
        'Portico': ['Light Fixture', 'Fan', 'Security Camera', 'Other'],
        'Garage': ['Washing Machine', 'Dryer', 'Vacuum Cleaner', 'Tools', 'Other'],
        'Office': ['Computer', 'Printer', 'Scanner', 'Shredder', 'Router', 'Other'],
        'Garden': ['Lawn Mower', 'Trimmer', 'Sprinkler System', 'Other'],
        'Other': ['Mobile Phone', 'Tablet', 'Smartwatch', 'Laptop', 'Other']
    };

    const recurrenceOptions = [
        { value: 'none', label: 'No Recurrence' },
        { value: 'monthly', label: 'Monthly' },
        { value: 'quarterly', label: 'Quarterly (3 months)' },
        { value: 'halfyearly', label: 'Half Yearly (6 months)' },
        { value: 'yearly', label: 'Yearly' }
    ];

    useEffect(() => {
        const loadData = async () => {
            try {
                const familiesData = await fetchFamilies();
                setFamilies(familiesData);

                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                let familyToSelect = null;
                
                if (selectedFamilyId && familiesData.length > 0) {
                    familyToSelect = familiesData.find(f => f.id === selectedFamilyId);
                }
                
                if (!familyToSelect && familiesData.length === 1) {
                    familyToSelect = familiesData[0];
                    localStorage.setItem('selectedFamilyId', familiesData[0].id);
                }
                
                if (!familyToSelect && familiesData.length > 0) {
                    localStorage.removeItem('selectedFamilyId');
                }
                
                setSelectedFamily(familyToSelect);
                
                // Set default shared with all family members
                if (familyToSelect && familyToSelect.members) {
                    const memberNames = familyToSelect.members
                        .filter(m => m && typeof m === 'object' && m.name)
                        .map(m => m.name);
                    setSharedWith(memberNames);
                }
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };
        loadData();
    }, []);

    // Load appliances when family changes
    useEffect(() => {
        const loadAppliances = async () => {
            if (!selectedFamily) return;
            
            try {
                const response = await fetchAppliances(selectedFamily.id);
                setAppliances(response.items || []);
            } catch (error) {
                console.error('Error loading appliances:', error);
            }
        };
        loadAppliances();
    }, [selectedFamily]);


    // Removed: No longer saving to localStorage, using API instead

    useEffect(() => {
        if (showAddModal || showPremiumModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [showAddModal, showPremiumModal]);

    // Reset shared with when family changes
    useEffect(() => {
        if (selectedFamily && selectedFamily.members) {
            const memberNames = selectedFamily.members
                .filter(m => m && typeof m === 'object' && m.name)
                .map(m => m.name);
            if (!editingAppliance) {
                setSharedWith(memberNames);
            }
        }
    }, [selectedFamily]);

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        localStorage.removeItem('selectedFamilyId');
        navigate('/login');
    };


    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploadedDocument({
                name: file.name,
                file: file,
                url: URL.createObjectURL(file)
            });
        }
    };

    const handleAddAppliance = async () => {
        if (!name || !appliance) {
            alert('Please fill in all required fields');
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            // Map frontend fields to backend schema
            const applianceData = {
                name,
                category: applianceType, // Location (Kitchen, Other, etc.)
                appliance_type: appliance || null, // Appliance Type (Refrigerator, TV, etc.)
                brand: brand || null,
                model: model || null,
                serial_number: uniqueId || null,
                purchase_date: purchaseDate || null,
                warranty_expiry: null, // Could be calculated or added to form
                maintenance_history: serviceDates.filter(sd => sd.date).map(sd => ({
                    date: sd.date,
                    recurrence: sd.recurrence
                })),
                notes: notes || null
            };

            if (editingAppliance) {
                const updatedAppliance = await updateAppliance(selectedFamily.id, editingAppliance.id, applianceData);
                setAppliances(appliances.map(a => a.id === editingAppliance.id ? updatedAppliance : a));
            } else {
                const createdAppliance = await createAppliance(selectedFamily.id, applianceData);
                setAppliances([...appliances, createdAppliance]);
            }

            resetForm();
        } catch (error) {
            console.error('Error saving appliance:', error);
            alert(`Failed to save appliance: ${error.message}`);
        }
    };

    const handleEditAppliance = (appliance) => {
        setEditingAppliance(appliance);
        setName(appliance.name);
        setBrand(appliance.brand || '');
        setModel(appliance.model || '');
        setUniqueId(appliance.serial_number || '');
        const location = appliance.category || 'Kitchen';
        setApplianceType(applianceTypes.includes(location) ? location : 'Other'); // Fallback if category not in list
        setAppliance(appliance.appliance_type || ''); // Backend: appliance_type
        setPurchaseDate(appliance.purchase_date ? String(appliance.purchase_date).slice(0, 10) : '');
        setServiceDates(appliance.maintenance_history && appliance.maintenance_history.length > 0
            ? appliance.maintenance_history.map((h) => ({
                date: h.date ? String(h.date).slice(0, 10) : '',
                recurrence: h.recurrence || 'none'
            }))
            : [{ date: '', recurrence: 'none' }]);
        setSharedWith([]); // Not stored in backend currently
        setNotes(appliance.notes || '');
        
        if (appliance.document) {
            if (appliance.document.file) {
                setDocumentOption('upload');
                setUploadedDocument(appliance.document);
            } else {
                setDocumentOption('select');
                setSelectedDocument(appliance.document);
            }
        } else {
            setDocumentOption('none');
        }
        
        setShowAddModal(true);
        setOpenMenuId(null);
    };

    const handleDeleteAppliance = async (id) => {
        if (!window.confirm('Are you sure you want to delete this appliance?')) {
            return;
        }

        if (!selectedFamily) {
            alert('Please select a family first');
            return;
        }

        try {
            await deleteAppliance(selectedFamily.id, id);
            setAppliances(appliances.filter(a => a.id !== id));
        } catch (error) {
            console.error('Error deleting appliance:', error);
            alert(`Failed to delete appliance: ${error.message}`);
        }
        
        setOpenMenuId(null);
    };

    const resetForm = () => {
        setName('');
        setBrand('');
        setModel('');
        setUniqueId('');
        setApplianceType('Kitchen');
        setAppliance('');
        setPurchaseDate('');
        setServiceDates([{ date: '', recurrence: 'none' }]);
        setDocumentOption('none');
        setUploadedDocument(null);
        setNotes('');
        setSelectedDocument(null);
        setEditingAppliance(null);
        setShowAddModal(false);
        
        // Reset to all family members
        if (selectedFamily && selectedFamily.members) {
            const memberNames = selectedFamily.members
                .filter(m => m && typeof m === 'object' && m.name)
                .map(m => m.name);
            setSharedWith(memberNames);
        }
        
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const addServiceDate = () => {
        setServiceDates([...serviceDates, { date: '', recurrence: 'none' }]);
    };

    const removeServiceDate = (index) => {
        if (serviceDates.length > 1) {
            setServiceDates(serviceDates.filter((_, i) => i !== index));
        }
    };

    const updateServiceDate = (index, field, value) => {
        const newServiceDates = [...serviceDates];
        newServiceDates[index][field] = value;
        setServiceDates(newServiceDates);
    };

    const getFamilyMembers = () => {
        if (!selectedFamily || !selectedFamily.members) return [];
        return selectedFamily.members.filter(member => member && typeof member === 'object' && member.name);
    };

    const toggleMemberAccess = (member) => {
        const memberName = typeof member === 'object' ? member.name : member;
        if (sharedWith.includes(memberName)) {
            setSharedWith(sharedWith.filter(m => m !== memberName));
        } else {
            setSharedWith([...sharedWith, memberName]);
        }
    };

    const getFilteredAppliances = () => {
        let filtered = [...appliances];

        if (searchQuery) {
            filtered = filtered.filter(a => 
                a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (a.category && a.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (a.brand && a.brand.toLowerCase().includes(searchQuery.toLowerCase()))
            );
        }

        return filtered;
    };

    const getNextServiceDates = (appliance) => {
        if (!appliance.serviceDates || appliance.serviceDates.length === 0) {
            return [];
        }

        const today = new Date();
        const allServiceDates = [];

        appliance.serviceDates.forEach(serviceDate => {
            if (!serviceDate.date) return;

            if (serviceDate.recurrence === 'none') {
                allServiceDates.push({
                    date: serviceDate.date,
                    recurrence: 'none'
                });
            } else {
                let dueDate = new Date(serviceDate.date);
                
                // Calculate next service date based on recurrence
                while (dueDate <= today) {
                    switch (serviceDate.recurrence) {
                        case 'monthly':
                            dueDate.setMonth(dueDate.getMonth() + 1);
                            break;
                        case 'quarterly':
                            dueDate.setMonth(dueDate.getMonth() + 3);
                            break;
                        case 'halfyearly':
                            dueDate.setMonth(dueDate.getMonth() + 6);
                            break;
                        case 'yearly':
                            dueDate.setFullYear(dueDate.getFullYear() + 1);
                            break;
                        default:
                            break;
                    }
                }
                
                allServiceDates.push({
                    date: dueDate.toISOString().split('T')[0],
                    recurrence: serviceDate.recurrence
                });
            }
        });

        // Sort by date
        allServiceDates.sort((a, b) => new Date(a.date) - new Date(b.date));
        return allServiceDates;
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <NavBar
                families={families}
                selectedFamily={selectedFamily}
                setSelectedFamily={setSelectedFamily}
                setShowPremiumModal={setShowPremiumModal}
                handleLogout={handleLogout}
            />

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Breadcrumb */}
                <div className="mb-4 flex items-center gap-2 text-sm">
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-gray-600 hover:text-primary-blue transition-colors"
                    >
                        Dashboard
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">Appliances & Devices</span>
                </div>

                {/* Header */}
                <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <h2 className="text-3xl font-bold text-gray-900">Appliances & Devices</h2>
                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                        {/* Search */}
                        <div className="relative flex-1 sm:flex-none">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search appliances..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent w-full sm:w-64"
                            />
                        </div>

                        {/* View Toggle */}
                        <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-blue text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                title="Grid view"
                            >
                                <Grid className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-blue text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                                title="List view"
                            >
                                <List className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Add Button */}
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                        >
                            <Plus className="w-5 h-5" />
                            Add Appliance
                        </button>
                    </div>
                </div>

                {/* Appliances List */}
                {getFilteredAppliances().length === 0 ? (
                    <div className="bg-white rounded-xl shadow-md p-12 text-center">
                        <Tv className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">No appliances yet. Click "Add Appliance" to get started!</p>
                    </div>
                ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {getFilteredAppliances().map((item) => {
                            const serviceDates = getNextServiceDates(item);
                            const nextService = serviceDates[0];
                            const isServiceDue = nextService && new Date(nextService.date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                            
                            return (
                                <div key={item.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                                    {item.category || 'N/A'}
                                                </span>
                                                {isServiceDue && (
                                                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                                                        Service Due
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-900">{item.name}</h3>
                                            {(item.brand || item.model) && (
                                                <p className="text-sm text-gray-600">
                                                    {[item.brand, item.model].filter(Boolean).join(' ')}
                                                </p>
                                            )}
                                            {(item.brand || item.model) && (
                                                <p className="text-xs text-gray-500">
                                                    {item.brand && item.brand}{item.brand && item.model && ' - '}{item.model && item.model}
                                                </p>
                                            )}
                                        </div>

                                        {/* Menu */}
                                        <div className="relative">
                                            <button
                                                onClick={() => setOpenMenuId(openMenuId === item.id ? null : item.id)}
                                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                            >
                                                <MoreVertical className="w-5 h-5 text-gray-600" />
                                            </button>

                                            {openMenuId === item.id && (
                                                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                    <button
                                                        onClick={() => handleEditAppliance(item)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                        <span>Edit</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteAppliance(item.id)}
                                                        className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        <span>Delete</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Details */}
                                    <div className="space-y-2 text-sm">
                                        {item.uniqueId && (
                                            <div className="text-gray-600">
                                                <span className="font-medium">ID:</span> {item.uniqueId}
                                            </div>
                                        )}
                                        
                                        {item.purchaseDate && (
                                            <div className="flex items-center gap-2 text-gray-600">
                                                <Calendar className="w-4 h-4" />
                                                <span>Purchased: {new Date(item.purchaseDate).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                        
                                        {serviceDates.length > 0 && (
                                            <div className="space-y-1">
                                                {serviceDates.map((service, index) => (
                                                    <div key={index} className="flex items-center gap-2 text-gray-600">
                                                        <Calendar className="w-4 h-4" />
                                                        <span>
                                                            Service: {new Date(service.date).toLocaleDateString()}
                                                            {service.recurrence !== 'none' && (
                                                                <span className="text-xs ml-1">
                                                                    ({recurrenceOptions.find(r => r.value === service.recurrence)?.label})
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {item.document && (
                                            <div className="flex items-center gap-2 text-gray-600">
                                                <FileText className="w-4 h-4" />
                                                <span className="truncate">{item.document.name}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Shared With */}
                                    {item.sharedWith && item.sharedWith.length > 0 && (
                                        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200">
                                            <span className="text-xs text-gray-500">Shared with:</span>
                                            <div className="flex -space-x-2">
                                                {item.sharedWith.slice(0, 4).map((member, index) => (
                                                    <div key={index} className="relative">
                                                        <MemberAvatar name={member} size="xs" className="border-2 border-white" />
                                                    </div>
                                                ))}
                                                {item.sharedWith.length > 4 && (
                                                    <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 text-xs font-bold border-2 border-white">
                                                        +{item.sharedWith.length - 4}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* List View */
                    <div className="space-y-3">
                        {getFilteredAppliances().map((item) => {
                            const serviceDates = getNextServiceDates(item);
                            const nextService = serviceDates[0];
                            const isServiceDue = nextService && new Date(nextService.date) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                            
                            return (
                                <div key={item.id} className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-shadow">
                                    <div className="flex items-start gap-4">
                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                                    {item.category || 'N/A'}
                                                </span>
                                                {isServiceDue && (
                                                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                                                        Service Due
                                                    </span>
                                                )}
                                            </div>
                                            
                                            <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
                                                {item.brand && <span>{item.brand}</span>}
                                                {item.brand && <span>{item.brand}</span>}
                                                {item.model && <span>{item.model}</span>}
                                                {item.uniqueId && <span>ID: {item.uniqueId}</span>}
                                            </div>
                                            
                                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                                                {item.purchaseDate && (
                                                    <span>Purchased: {new Date(item.purchaseDate).toLocaleDateString()}</span>
                                                )}
                                                {serviceDates.length > 0 && (
                                                    <span>
                                                        Next Service: {new Date(serviceDates[0].date).toLocaleDateString()}
                                                        {serviceDates.length > 1 && ` (+${serviceDates.length - 1} more)`}
                                                    </span>
                                                )}
                                                {item.document && <span>📄 {item.document.name}</span>}
                                            </div>
                                        </div>

                                        {/* Shared With */}
                                        {item.sharedWith && item.sharedWith.length > 0 && (
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <div className="flex -space-x-2">
                                                    {item.sharedWith.slice(0, 3).map((member, index) => (
                                                        <div key={index} className="relative">
                                                            <MemberAvatar name={member} size="sm" className="border-2 border-white" />
                                                        </div>
                                                    ))}
                                                    {item.sharedWith.length > 3 && (
                                                        <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-700 text-xs font-bold border-2 border-white">
                                                            +{item.sharedWith.length - 3}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Menu */}
                                        <div className="relative flex-shrink-0">
                                            <button
                                                onClick={() => setOpenMenuId(openMenuId === item.id ? null : item.id)}
                                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                            >
                                                <MoreVertical className="w-5 h-5 text-gray-600" />
                                            </button>

                                            {openMenuId === item.id && (
                                                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-10">
                                                    <button
                                                        onClick={() => handleEditAppliance(item)}
                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                        <span>Edit</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteAppliance(item.id)}
                                                        className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                        <span>Delete</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-900">
                                {editingAppliance ? 'Edit' : 'Add'} Appliance
                            </h2>
                            <button
                                onClick={resetForm}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-5">
                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g., Samsung Refrigerator"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Brand */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                                <input
                                    type="text"
                                    value={brand}
                                    onChange={(e) => setBrand(e.target.value)}
                                    placeholder="e.g., Samsung, LG, Sony"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Model */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                                <input
                                    type="text"
                                    value={model}
                                    onChange={(e) => setModel(e.target.value)}
                                    placeholder="e.g., ABC-123"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Unique Identifier */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Unique Identifier</label>
                                <input
                                    type="text"
                                    value={uniqueId}
                                    onChange={(e) => setUniqueId(e.target.value)}
                                    placeholder="e.g., Serial number, Asset ID"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Type of Appliance */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
                                <select
                                    value={applianceType}
                                    onChange={(e) => {
                                        setApplianceType(e.target.value);
                                        setAppliance(''); // Reset appliance when type changes
                                    }}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                >
                                    {applianceTypes.map((type) => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Appliance */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Appliance Type *</label>
                                <select
                                    value={appliance}
                                    onChange={(e) => setAppliance(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                >
                                    <option value="">Select appliance type</option>
                                    {(() => {
                                        const options = applianceCategories[applianceType] || [];
                                        const hasCurrent = options.includes(appliance);
                                        const opts = hasCurrent ? options : (appliance ? [appliance, ...options] : options);
                                        return opts.map((app) => (
                                            <option key={app} value={app}>{app}</option>
                                        ));
                                    })()}
                                </select>
                            </div>

                            {/* Purchase Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Date</label>
                                <input
                                    type="date"
                                    value={purchaseDate}
                                    onChange={(e) => setPurchaseDate(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                />
                            </div>

                            {/* Service Dates */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">Service Dates</label>
                                    <button
                                        type="button"
                                        onClick={addServiceDate}
                                        className="text-sm text-primary-blue hover:text-blue-700 font-medium flex items-center gap-1"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Service Date
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {serviceDates.map((serviceDate, index) => (
                                        <div key={index} className="p-3 border border-gray-200 rounded-lg space-y-3">
                                            <div className="flex items-start gap-2">
                                                <div className="flex-1 space-y-3">
                                                    <div>
                                                        <label className="block text-xs text-gray-600 mb-1">Service Date</label>
                                                        <input
                                                            type="date"
                                                            value={serviceDate.date}
                                                            onChange={(e) => updateServiceDate(index, 'date', e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                        />
                                                    </div>
                                                    {serviceDate.date && (
                                                        <div>
                                                            <label className="block text-xs text-gray-600 mb-1">Recurrence</label>
                                                            <select
                                                                value={serviceDate.recurrence}
                                                                onChange={(e) => updateServiceDate(index, 'recurrence', e.target.value)}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent text-sm"
                                                            >
                                                                {recurrenceOptions.map((option) => (
                                                                    <option key={option.value} value={option.value}>{option.label}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    )}
                                                </div>
                                                {serviceDates.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeServiceDate(index)}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                                                        title="Remove service date"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Document Options */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Document</label>
                                <div className="space-y-3">
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setDocumentOption('none')}
                                            className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                                                documentOption === 'none'
                                                    ? 'border-primary-blue bg-blue-50 text-primary-blue'
                                                    : 'border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            No Document
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setDocumentOption('upload')}
                                            className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                                                documentOption === 'upload'
                                                    ? 'border-primary-blue bg-blue-50 text-primary-blue'
                                                    : 'border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            <Upload className="w-4 h-4 inline mr-2" />
                                            Upload New
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setDocumentOption('select');
                                                alert('Document selection from existing documents coming soon!');
                                            }}
                                            className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                                                documentOption === 'select'
                                                    ? 'border-primary-blue bg-blue-50 text-primary-blue'
                                                    : 'border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            <FileText className="w-4 h-4 inline mr-2" />
                                            Select Existing
                                        </button>
                                    </div>

                                    {documentOption === 'upload' && (
                                        <div>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                onChange={handleFileSelect}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                                            />
                                            {uploadedDocument && (
                                                <p className="mt-2 text-sm text-gray-600">
                                                    Selected: {uploadedDocument.name}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Add any additional notes about this appliance..."
                                    rows="4"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-blue focus:border-transparent resize-none"
                                />
                            </div>

                            {/* Share With */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Share with Family Members (Default: All)
                                </label>
                                {getFamilyMembers().length > 0 ? (
                                    <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {getFamilyMembers().map((member, index) => (
                                            <MemberSelector
                                                key={index}
                                                name={member.name}
                                                photo={member.photo}
                                                selected={sharedWith.includes(member.name)}
                                                    onChange={() => toggleMemberAccess(member)}
                                                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                                                />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 text-center">
                                        <p className="text-sm text-gray-600">
                                            No family members found. Add members to your family in the{' '}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    resetForm();
                                                    navigate('/famlis');
                                                }}
                                                className="text-primary-blue hover:underline font-medium"
                                            >
                                                Mi Famlis
                                            </button>
                                            {' '}page.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={resetForm}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddAppliance}
                                disabled={!name || !appliance}
                                className="px-4 py-2 bg-primary-blue text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {editingAppliance ? 'Update' : 'Add'} Appliance
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Premium Modal */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Appliances;

